import { PiSealCheckFill } from 'react-icons/pi';
import { cn } from '@v2/utils/cn';
import Breadcrumb from '@v2/design-system/ui/breadcrumb';
import { Title } from 'rizzui/typography';

export type UserSettingsTab = 'profile' | 'password' | 'roles';

export type UserAccountTab = {
    key: UserSettingsTab;
    label: string;
};

type UserSummary = {
    name: string;
    avatar_url: string | null;
    subtitle?: string | null;
    verified?: boolean;
};

type Props = {
    pageTitle: string;
    breadcrumb: Array<{ name: string; href?: string }>;
    tabs: UserAccountTab[];
    activeTab: UserSettingsTab;
    onTabChange: (tab: UserSettingsTab) => void;
    user: UserSummary;
    headerAction?: React.ReactNode;
    children: React.ReactNode;
};

const FALLBACK = '/img/favicon.png';

export default function UserAccountLayout({
    pageTitle,
    breadcrumb,
    tabs,
    activeTab,
    onTabChange,
    user,
    headerAction,
    children,
}: Props) {
    return (
        <div className="space-y-6">
            <header className="mb-2">
                <Title as="h2" className="mb-2 text-[22px] font-bold text-gray-900 dark:text-gray-100 lg:text-2xl">
                    {pageTitle}
                </Title>
                <Breadcrumb separator="" separatorVariant="circle" className="flex-wrap">
                    {breadcrumb.map((item) => (
                        <Breadcrumb.Item key={item.name} {...(item.href ? { href: item.href } : {})}>
                            {item.name}
                        </Breadcrumb.Item>
                    ))}
                </Breadcrumb>
            </header>

            <article className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
                {/* Banner */}
                <div
                    className="h-36 bg-gradient-to-r from-[#FFF7ED] via-[#FEF3C7] to-[#FCE7F3] dark:from-amber-950/40 dark:via-yellow-950/30 dark:to-pink-950/40"
                    aria-hidden
                />

                <div className="flex flex-wrap items-end justify-between gap-4 px-6 pb-5">
                    <div className="-mt-12 flex min-w-0 items-end gap-4">
                        <div className="size-24 shrink-0 overflow-hidden rounded-full border-4 border-white bg-gray-100 shadow-sm dark:border-gray-900 dark:bg-gray-700">
                            <img
                                src={user.avatar_url || FALLBACK}
                                alt={user.name}
                                className="size-full object-cover"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = FALLBACK;
                                }}
                            />
                        </div>
                        <div className="min-w-0 pb-1">
                            <p className="flex items-center gap-1.5 truncate text-lg font-bold text-gray-900 dark:text-gray-100">
                                {user.name}
                                {user.verified !== false && (
                                    <PiSealCheckFill className="size-5 shrink-0 text-gray-900 dark:text-gray-300" aria-label="Verificado" />
                                )}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {user.subtitle ?? 'Atualize sua foto e dados pessoais.'}
                            </p>
                        </div>
                    </div>
                    {headerAction}
                </div>

                {/* Tabs */}
                <nav className="flex gap-1 overflow-x-auto border-b border-gray-200 px-6 dark:border-gray-700">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => onTabChange(tab.key)}
                            className={cn(
                                'whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition',
                                activeTab === tab.key
                                    ? 'border-gray-900 text-gray-900 dark:border-gray-100 dark:text-gray-100'
                                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200',
                            )}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>

                <div className="px-6 py-2">{children}</div>
            </article>
        </div>
    );
}
